type EventHandler<K extends keyof HTMLElementEventMap> = {
    type: K;
    listener: (event: HTMLElementEventMap[K]) => void;
};

export type ElementProperties<K extends keyof HTMLElementEventMap> = {
    id?: string;
    classes?: string[];
    href?: string;
    src?: string;
    type?: string;
    ariaControls?: string;
    ariaHidden?: string;
    ariaExpanded?: string;
    tabIndex?: number;
    innerText?: string;
    value?: string;
    role?: string;
    title?: string;
    alt?: string;
    children?: (HTMLElement | null)[];
    disabled?: boolean;
    target?: string;
    properties?: {
        key: string,
        value: string;
    }[];
    dataset?: {
        [key: string]: string;
    };
    ariaPosInSet?: string;
    style?: Partial<CSSStyleDeclaration>,
    handlers?: EventHandler<K>[];
}

export const createElement = <K extends keyof HTMLElementTagNameMap>(tagName: K, options?: ElementProperties<any>): HTMLElementTagNameMap[K] => {
    const element = document.createElement(tagName);
    if (options?.id) {
        element.id = options.id;
    }
    if (options?.classes) {
        element.classList.add(...options.classes);
    }
    if (options?.href && 'href' in element) {
        element.href = options.href;
    }
    if (options?.src && 'src' in element) {
        element.src = options.src
    }
    if (options?.style) {
        for (const key of Object.keys(options.style) as Array<keyof CSSStyleDeclaration>) {

            if (options.style[key] !== undefined) {
                (element.style as any)[key] = options.style[key];
            }
        }
    }
    if (options?.type && 'type' in element) {
        (element as any).type = options.type;
    }
    if (options?.ariaControls) {
        element.setAttribute('aria-controls', options.ariaControls);
    }
    if (options?.ariaHidden !== undefined) {
        element.ariaHidden = options.ariaHidden;
    }
    if (options?.ariaExpanded !== undefined) {
        element.ariaExpanded = options.ariaExpanded;
    }
    if (options?.tabIndex !== undefined) {
        element.tabIndex = options.tabIndex;
    }
    if (options?.innerText) {
        element.innerText = options.innerText;
    }
    if (options?.value && 'value' in element) {
        element.value = options.value;
    }
    if (options?.title) {
        element.title = options.title;
    }
    if (options?.alt && 'alt' in element) {
        element.alt = options.alt;
    }
    if (options?.target && 'target' in element) {
        element.target = options.target;
    }
    if (options?.properties) {
        for (const property of options.properties) {
            if (property.key && property.key in element) {
                (element as any)[property.key] = property.value;
            }
        }
    }
    if (options?.role) {
        element.role = options.role;
    }
    if (options?.ariaPosInSet) {
        element.ariaPosInSet = options.ariaPosInSet;
    }
    if (options?.disabled && 'disabled' in element) {
        element.disabled = element.disabled;
    }
    if (options?.children) {
        for (let child of options.children) {
            if (child !== null) {
                element.appendChild(child);
            }
        }
    }
    if (options?.handlers) {
        for (const handler of options.handlers) {
            element.addEventListener(handler.type, handler.listener);
        }
    }

    return element;
}